<template>
  <div class="appeal-detail-container" v-loading="loading">
    <!-- 诉求详情 -->
    <el-card v-if="detail.appealId">
      <div slot="header" class="clearfix">
        <span>诉求详情</span>
        <el-button style="float: right;" size="small" @click="goBack">返回</el-button>
      </div>

      <h3 class="appeal-title">{{ detail.title }}</h3>
      <el-descriptions :column="3" border>
        <el-descriptions-item label="状态">{{ detail.stateName }}</el-descriptions-item>
        <el-descriptions-item label="小区">{{ detail.communityName }}</el-descriptions-item>
        <el-descriptions-item label="分类">{{ detail.typeName }}</el-descriptions-item>
        <el-descriptions-item label="提交人">{{ detail.isAnonymous === '1' ? '匿名' : detail.createUserName }}</el-descriptions-item>
        <el-descriptions-item label="联系电话">{{ detail.tel }}</el-descriptions-item>
        <el-descriptions-item label="提交时间">{{ detail.createTime }}</el-descriptions-item>
      </el-descriptions>

      <div class="content-block">
        <label>诉求内容：</label>
        <div class="content-text">{{ detail.content }}</div>
      </div>

      <div v-if="detail.photos && detail.photos.length > 0" class="image-block">
        <label>诉求图片：</label>
        <div class="image-list">
          <el-image v-for="(photo, index) in detail.photos" :key="`p-${index}`" :src="getImageUrl(photo)"
            fit="cover" style="width: 120px; height: 120px; margin-right: 10px;"
            :preview-src-list="getPreviewList(detail.photos)" />
        </div>
      </div>
    </el-card>

    <!-- 回复信息 -->
    <el-card v-if="showReplyCard" class="reply-card">
      <div slot="header">回复信息</div>

      <div v-if="detail.state === 'C' && detail.replyContent">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="回复人">{{ detail.replyUserName }}</el-descriptions-item>
          <el-descriptions-item label="回复时间">{{ detail.replyTime }}</el-descriptions-item>
        </el-descriptions>
        <div class="content-block">
          <label>回复内容：</label>
          <div class="content-text">{{ detail.replyContent }}</div>
        </div>
        <div v-if="detail.replyPhotos && detail.replyPhotos.length > 0" class="image-block">
          <label>回复图片：</label>
          <div class="image-list">
            <el-image v-for="(photo, index) in detail.replyPhotos" :key="`rp-${index}`" :src="getImageUrl(photo)"
              fit="cover" style="width: 120px; height: 120px; margin-right: 10px;"
              :preview-src-list="getPreviewList(detail.replyPhotos)" />
          </div>
        </div>
      </div>

      <div v-if="detail.state === 'R' && detail.cancelRemark" class="sub-section">
        <h4 class="sub-title">撤销申请</h4>
        <div class="content-block">
          <label>撤销说明：</label>
          <div class="content-text">{{ detail.cancelRemark }}</div>
        </div>
      </div>

      <div v-if="detail.state === 'X' && detail.cancelAuditRemark" class="sub-section">
        <h4 class="sub-title">撤销审核</h4>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="审核人">{{ detail.cancelAuditUserName }}</el-descriptions-item>
          <el-descriptions-item label="审核时间">{{ detail.cancelAuditTime }}</el-descriptions-item>
        </el-descriptions>
        <div class="content-block">
          <label>审核说明：</label>
          <div class="content-text">{{ detail.cancelAuditRemark }}</div>
        </div>
      </div>
    </el-card>

    <!-- 事件轨迹 -->
    <el-card v-if="detail.events && detail.events.length > 0" class="timeline-card">
      <div slot="header">事件轨迹</div>
      <el-timeline class="appeal-timeline">
        <el-timeline-item v-for="(event, index) in detail.events" :key="`e-${index}`"
          :timestamp="event.createTime" placement="top">
          <div class="event-title">{{ event.eventTypeName || event.eventType }} - {{ event.operatorName || '系统' }}</div>
          <div v-if="event.remark" class="event-remark">{{ event.remark }}</div>
        </el-timeline-item>
      </el-timeline>
    </el-card>

    <!-- 操作区 -->
    <el-card v-if="detail.state === 'W' || detail.state === 'R'" class="action-card">
      <div slot="header">{{ detail.state === 'R' ? '审核撤销申请' : '回复诉求' }}</div>

      <!-- 回复表单 -->
      <el-form v-if="detail.state === 'W'" ref="replyForm" :model="replyForm" :rules="replyRules" label-width="90px">
        <el-form-item label="回复内容" prop="replyContent">
          <el-input v-model="replyForm.replyContent" type="textarea" :rows="4" placeholder="请输入回复内容" />
        </el-form-item>
        <el-form-item label="回复图片">
          <div class="upload-image-container">
            <div v-for="(photo, index) in replyPhotos" :key="`up-${index}`" class="image-item">
              <el-image :src="photo.preview" fit="cover" style="width: 100px; height: 100px;"
                :preview-src-list="replyPhotos.map(p => p.preview)" />
              <i class="el-icon-delete delete-icon" @click="removeReplyPhoto(index)" />
            </div>
            <div v-if="replyPhotos.length < 6" class="upload-button" @click="triggerUpload">
              <i class="el-icon-plus" />
            </div>
          </div>
          <input ref="fileInput" type="file" accept="image/*" hidden @change="handleFileChange" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="submitReply">提交回复</el-button>
        </el-form-item>
      </el-form>

      <!-- 审核表单 -->
      <el-form v-if="detail.state === 'R'" ref="auditForm" :model="auditForm" :rules="auditRules" label-width="90px">
        <el-form-item label="审核结果" prop="auditState">
          <el-radio-group v-model="auditForm.auditState">
            <el-radio label="X">同意撤销</el-radio>
            <el-radio label="W">拒绝撤销</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="审核说明" prop="auditRemark">
          <el-input v-model="auditForm.auditRemark" type="textarea" :rows="3" placeholder="请输入审核说明" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="submitAudit">提交审核</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { queryAreaAppealDetail, replyAreaAppeal, auditCancelAreaAppeal, uploadImage } from '@/api/community/areaAppealApi'

export default {
  name: 'AreaAppealDetail',
  data() {
    return {
      loading: false,
      appealId: '',
      detail: {},
      replyForm: {
        replyContent: ''
      },
      replyRules: {
        replyContent: [
          { required: true, message: '请输入回复内容', trigger: 'blur' }
        ]
      },
      replyPhotos: [],
      auditForm: {
        auditState: 'X',
        auditRemark: ''
      },
      auditRules: {
        auditState: [
          { required: true, message: '请选择审核结果', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    showReplyCard() {
      const d = this.detail
      if (!d || !d.state) return false
      if (d.state === 'C' && d.replyContent) return true
      if (d.state === 'R' && d.cancelRemark) return true
      if (d.state === 'X' && d.cancelAuditRemark) return true
      return false
    }
  },
  created() {
    this.appealId = this.$route.query.appealId
    this.loadDetail()
  },
  methods: {
    loadDetail() {
      if (!this.appealId) {
        this.$message.error('诉求ID不能为空')
        return
      }
      this.loading = true
      queryAreaAppealDetail(this.appealId).then(data => {
        this.detail = data || {}
      }).catch(error => {
        this.$message.error(error.message || '获取诉求详情失败')
      }).finally(() => {
        this.loading = false
      })
    },
    getImageUrl(photo) {
      const fileId = photo.fileRealName || photo.fileSaveName || photo.url || photo
      if (!fileId) return ''
      if (typeof fileId === 'string' && (fileId.indexOf('http') === 0 || fileId.indexOf('base64,') > -1)) {
        return fileId
      }
      return `/callComponent/download/getFile/file?fileId=${fileId}&communityId=-1&time=${new Date().getTime()}`
    },
    getPreviewList(photos) {
      return (photos || []).map(p => this.getImageUrl(p))
    },
    goBack() {
      this.$router.back()
    },
    triggerUpload() {
      this.$refs.fileInput.click()
    },
    handleFileChange(event) {
      const file = event.target.files[0]
      if (!file) return
      if (file.size > 5 * 1024 * 1024) {
        this.$message.error('图片大小不能超过5MB')
        return
      }
      if (this.replyPhotos.length >= 6) {
        this.$message.error('最多上传6张图片')
        return
      }
      const reader = new FileReader()
      reader.onload = (e) => {
        const preview = e.target.result
        const formData = new FormData()
        formData.append('uploadFile', file)
        uploadImage(formData).then(fileId => {
          this.replyPhotos.push({ fileId, preview })
        }).catch(error => {
          this.$message.error(error.message || '上传图片失败')
        })
      }
      reader.readAsDataURL(file)
      event.target.value = null
    },
    removeReplyPhoto(index) {
      this.replyPhotos.splice(index, 1)
    },
    submitReply() {
      this.$refs.replyForm.validate(valid => {
        if (!valid) return
        const data = {
          appealId: this.appealId,
          replyContent: this.replyForm.replyContent,
          photos: this.replyPhotos.map(p => p.fileId)
        }
        replyAreaAppeal(data).then(() => {
          this.$message.success('回复成功')
          this.loadDetail()
        }).catch(error => {
          this.$message.error(error.message || '回复失败')
        })
      })
    },
    submitAudit() {
      this.$refs.auditForm.validate(valid => {
        if (!valid) return
        const data = {
          appealId: this.appealId,
          auditState: this.auditForm.auditState,
          auditRemark: this.auditForm.auditRemark
        }
        auditCancelAreaAppeal(data).then(() => {
          this.$message.success('审核成功')
          this.loadDetail()
        }).catch(error => {
          this.$message.error(error.message || '审核失败')
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.appeal-detail-container {
  padding: 20px;

  .el-card {
    margin-bottom: 20px;
  }

  .appeal-title {
    margin: 0 0 20px 0;
    font-size: 18px;
    font-weight: bold;
    color: #303133;
  }

  .content-block {
    margin-top: 15px;

    label {
      font-weight: bold;
      color: #606266;
    }

    .content-text {
      margin-top: 8px;
      padding: 12px;
      background: #f5f7fa;
      border-radius: 4px;
      line-height: 1.6;
      white-space: pre-wrap;
      text-align: left;
    }
  }

  .image-block {
    margin-top: 15px;

    label {
      font-weight: bold;
      color: #606266;
      display: block;
      margin-bottom: 8px;
    }

    .image-list {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }
  }

  .reply-card {
    .sub-section {
      margin-top: 20px;

      .sub-title {
        margin: 0 0 12px 0;
        font-size: 15px;
        font-weight: bold;
        color: #303133;
        border-left: 3px solid #67c23a;
        padding-left: 8px;
      }
    }
  }

  .timeline-card {
    .appeal-timeline {
      text-align: left;

      .event-title {
        text-align: left;
        font-weight: 500;
      }

      .event-remark {
        margin-top: 8px;
        color: #606266;
        text-align: left;
        white-space: pre-wrap;
        word-break: break-all;
        line-height: 1.6;
      }
    }
  }

  .upload-image-container {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;

    .image-item {
      position: relative;
      width: 100px;
      height: 100px;

      .delete-icon {
        position: absolute;
        top: -10px;
        right: -10px;
        color: #f56c6c;
        cursor: pointer;
        font-size: 16px;
        background: white;
        border-radius: 50%;
        padding: 2px;
      }
    }

    .upload-button {
      width: 100px;
      height: 100px;
      border: 1px dashed #dcdfe6;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #909399;
      font-size: 24px;

      &:hover {
        border-color: #409eff;
        color: #409eff;
      }
    }
  }
}
</style>
